import classNames from 'classnames'
import React, { ReactNodeArray, ReactNode } from 'react'
import styled from 'styled-components'
import { getMeasureFromArray } from '../component-utils'

const textSizes = {
  tiny: '0.625em', // 10px
  small: '0.75em', // 12px
  medium: '0.875em', // 14px
  large: '1em', // 16px
  huge: '1.125em', // 18px
}

const textWeight = {
  bold: '700',
  semibold: '600',
  regular: '400',
  light: '300',
  lighter: 'lighter',
}

const textFontStyle = {
  normal: 'normal',
  italic: 'italic',
}

export interface IText {
  /**
   *  HTML elements
   */
  as?: keyof JSX.IntrinsicElements
  /**
   *  Desired info goes here
   */
  children: ReactNodeArray | ReactNode
  /**
   *  huge 18, large 16, medium 14, small 12, tiny 10
   */
  size?: keyof typeof textSizes
  /**
   *  bold 700, semibold 600, regular 400, light 300, lighter
   */
  weight?: keyof typeof textWeight
  /**
   *  normal and italic
   */
  fontStyle?: keyof typeof textFontStyle
  /**
   *  Make text uppercase
   */
  uppercase?: boolean
  /**
   *  Make text lowercase
   */
  lowercase?: boolean
  /**
   *  Make text capitalize
   */
  capitalize?: boolean
  /**
   *  Center text
   */
  center?: boolean
  /**
   * Specifies how white-space inside an element is handled.
   * normal|nowrap|pre|pre-line|pre-wrap|initial|inherit
   */
  whiteSpace?: string
  /**
   * Margins: number * 0.5rem [top, right, bottom, left]
   * @default [0,0,0,0]
   */
  margin?: [number, number, number, number]
  /**
   * Custom class
   */
  className?: string
}

const StyledText = styled.div<IText>`
  font-size: ${({ size }) => textSizes[size || 'medium']};
  font-weight: ${({ weight }) => textWeight[weight || 'regular']};
  font-style: ${({ fontStyle }) => textFontStyle[fontStyle || 'normal']};
  white-space: ${({ whiteSpace }) => whiteSpace || 'normal'};
  margin: ${({ margin }) => getMeasureFromArray(margin || [0, 0, 0, 0])};
  // TODO: Add coloe with themes
  &.uppercase {
    text-transform: uppercase;
  }

  &.capitalize {
    text-transform: capitalize;
  }

  &.lowercase {
    text-transform: lowercase;
  }

  &.center {
    text-align: center;
  }
`

const Text = ({
  as = 'p',
  children,
  size,
  weight,
  fontStyle,
  uppercase,
  lowercase,
  capitalize,
  center,
  whiteSpace,
  margin = [0, 0, 0, 0],
  className,
}: IText) => {
  const textClasses = classNames('Text', className, {
    uppercase,
    lowercase,
    capitalize,
    center,
  })

  return (
    <StyledText
      className={textClasses}
      as={as}
      size={size}
      weight={weight}
      fontStyle={fontStyle}
      whiteSpace={whiteSpace}
      margin={margin}
    >
      {children}
    </StyledText>
  )
}

export default Text
